import {create} from "zustand";
import {router} from "@inertiajs/react";
import isEmpty from "is-empty";
import {isDesktop} from "@/Helpers/Function";
import RootStores from "@/Stores/RootStores";

export const useSearchStore = create((set, get) => ({
    /**
     * Поисковой запрос
     */
    searchQuery: '',

    /**
     * Тип поиска (start, search, suggestions)
     */
    searchType: 'start',

    /**
     * Поисковой запрос, для фронта (вывод для пользователя)
     */
    searchQueryOutput: '',

    /**
     * Тип поисковых данных
     */
    searchValueType: 'search',

    /**
     * Список поисковых предложений (подсказки)
     */
    searchSuggestions: [],

    /**
     * История поисковых запросов
     */
    searchHistoryData: [],

    /**
     * Активная страница, используется на фронте (пагинация)
     */
    activePage: 1,

    /**
     * Активная страница, используется для запроса на сервер (пагинация)
     */
    page: 1,

    /**
     * Сколько показывать товаров на странице (пагинация)
     */
    perPage: 40,

    /**
     * Сколько показывать товаров на странице, неизменяемое значение (default)
     */
    perPageStart: 40,

    /**
     * Данные для построения фасетных фильтров
     */
    facetFilterData: {},

    /**
     * Набранные фильтры
     */
    staticFilterParams: {
        category: [],
        view: [],
        inserts: [],
        insertsColor: [],
        metal: [],
        sex: [],
        size: [],
        viewDesign: [],
        thematics: [],
        collection: [],
        weaving: [],
        price: [], // [{value_min: 601, value_max: 1412616}]
    },
    filterParams: {},

    /**
     * Текущая сортировка
     */
    sort: ['hit', 'desc'],

    /**
     * Данные для отображения сортировки
     */
    dataSort: [],

    /**
     * Название выбранной сортировки
     */
    selectedSortingName: 'По популярности',

    /**
     * Фокусировка инпута
     */
    focus: false,

    /**
     * Всплывашка для поиска с историей ввода или подсказками
     */
    hint: false,

    /**
     * Лоадер для поиска, пока не пришел ответ с сервера
     */
    loader: false,

    /**
     * Фильтры
     * new Map() - это коллекция ключ/значение, как и Object.
     * Но основное отличие в том, что Map позволяет использовать ключи любого типа.
     */
    filtersMap: new Map(),

    selectedFilters: [],

    getSearchData: (preserveScroll = false) => {
        const {
            searchQuery,
            searchType,
            page,
            perPage,
            filterParams,
            staticFilterParams,
            sort,
        } = get();

        const url = searchQuery ? `/search?search=${encodeURIComponent(searchQuery || '')}` : `/search`;
        router.post(
            url,
            {
                queryString: searchQuery,
                searchType: searchType,
                page: page,
                perPage: perPage,
                filterParams: !isEmpty(filterParams) ? filterParams : staticFilterParams,
                sort: sort,
            },
            {
                preserveScroll: preserveScroll,
                onSuccess: (page) => {
                    const {props = {}} = page;
                    get().setSearchData(props);
                }
            }
        );
    },

    /**
     * Обработка параметров url, если перешли по ссылке вида - https://platina-jewelry/search?search=пантера
     * @param props
     * @returns {null}
     */
    onChangeSearchParams: ({facetFilterData = {}}) => {
        const searchParams = new URLSearchParams(window.location.search);
        if (!searchParams.has('search')) return null;

        let queryString = searchParams.get('search');
        if (isEmpty(queryString)) return null;

        set({
            searchQuery: queryString,
            searchType: 'search',
            searchQueryOutput: queryString,
            facetFilterData: facetFilterData,
        });
    },

    /**
     * Обновление данных после запроса
     * @param props
     */
    setSearchData: (props = {}) => {
        const {searchType} = get();
        const {searchData = {}} = props;
        const {facetFilterData = {}} = searchData;
        // const {catalogStore = {}} = RootStores;

        // // Что бы не ломался каталог
        // catalogStore.deleteFilters();
        // selectedFilters.forEach((el) => {
        //     catalogStore.addOrRemoveFilter(el);
        // });

        if (searchType === 'search') {
            set({
                facetFilterData: facetFilterData,
            });
        }
    },

    /**
     * Изменение поискового запроса
     * @param value
     */
    setSearchQuery: (value = '') => set(() => ({searchQuery: value})),

    /**
     * Изменение типа поиска
     * @param type
     */
    setSearchType: (type = '') => set(() => ({searchType: type})),

    /**
     * Поисковой запрос, для фронта (вывод для пользователя)
     * @param type
     */
    setSearchQueryOutput: (type = '') => set(() => ({searchQueryOutput: get().searchQuery})),

    /**
     * Безопасно получить значения фильтра по ключу (Возвращает [] если нет данных или это не массив)
     * @param key
     * @returns {*|*[]}
     */
    getFilterValues: (key) => {
        const data = get().facetFilterData[key];
        return Array.isArray(data) ? data : [];
    },

    /**
     * Безопасно получить весь фильтр (вдруг не массив, например цена - price)
     * @param key
     * @returns {*|null}
     */
    getSafeFacet: (key) => {
        const data = get().facetFilterData;
        return Object.prototype.hasOwnProperty.call(data, key) ? data[key] : null;
    },

    /**
     * Проверяем, должен ли быть видим фильтр
     * @param filter
     * @returns {boolean}
     */
    isFilterVisible: (filter) => {
        if (!filter?.key || !filter?.type) return false;

        const {key, type} = filter;

        // Общая проверка "visible"
        const baseVisible = !!filter.visible;
        const deviceVisible = isDesktop() ? filter.visibleDesktop : filter.visibleMobile;

        if (!baseVisible || deviceVisible === false) return false;

        // фильтр диапазона (например price)
        if (type === 'range') {
            const facet = get().getSafeFacet(key);
            if (
                facet &&
                typeof facet.value_min === 'number' &&
                typeof facet.value_max === 'number'
            ) {
                return !(facet.value_min === 0 && facet.value_max === 0);
            }
            return false;
        }

        // обычные фильтры
        return get().getFilterValues(key).length > 0;
    },

    /**
     * Обработка ввода данных
     * @param e
     */
    searchOnChange: (e) => {
        const value = e.target.value;

        set({
            searchQuery: value,
            searchType: 'suggestions',
            hint: true
        });
    },

    /**
     * Событие фокус у поля поиска
     * @param e
     */
    searchOnFocus: (e) => {
        set({
            hint: true
        });
    },

    /**
     * Потеря фокуса у поля поиска
     * @param e
     */
    searchOnBlur: (e) => {
        set({
            hint: false
        });
    },

    changeSorting: (el) => {
        const {label, sortField, sortType} = el;
        const {perPageStart, getSearchData} = get();

        // Изменение данных
        set({
            searchType: 'search',
            activePage: 1,
            page: 1,
            perPage: perPageStart,
            selectedSortingName: label,
            sort: [sortField, sortType]
        });

        // Запрос на сервер
        getSearchData();
    },

    /**
     * Сбрасываем/Переключаем пагинацию
     * @param pageNumber
     */
    changePagination: (pageNumber = 1) => {
        const {perPageStart, getSearchData} = get();

        set({
            searchType: 'search',
            activePage: pageNumber,
            page: pageNumber,
            perPage: perPageStart,
        });

        getSearchData();
    },

    /**
     * Используется в фильтрах, общий метод (обработка radio, checkbox, range)
     * @param filter
     * @param dependencies
     */
    onChangeFilter: (filter, dependencies = []) => {
        const {changePagination, addOrRemoveFilter, getFilters, getSearchData} = get();
        changePagination();
        addOrRemoveFilter(filter, dependencies);
        getFilters();
        getSearchData();
    },

    /**
     * Обработчик потери фокуса (Тип фильтра - диапазон)
     * @param newRange
     * @param filterOption
     */
    onBlurRange: (newRange, filterOption) => {
        const {addOrRemoveFilter, getFilters, getSearchData} = get();
        addOrRemoveFilter({...filterOption, ...newRange});
        getFilters();
        getSearchData();
    },

    addOrRemoveFilter: (filter, dependencies = []) => {
        const {
            key = '', // Уникальный ключ фильтра, например 'category' или 'inserts'
            type = '', // Тип фильтра — 'radio' или 'checked'
            value = '' // Значение внутри фильтра, например 'Броши' или 'Фианит'
        } = filter;
        const state = get();

        const map = new Map(state.filtersMap);

        // Если фильтр по ключу ещё не добавлен — создаём его с пустым набором значений
        if (!map.has(key)) {
            map.set(key, {
                type,
                range: [],
                values: new Set(), // Set — для удобного добавления/удаления без дубликатов
            });
        }

        // Получаем текущую запись фильтра из карты
        const entry = map.get(key);

        // Проверяем, выбрано ли уже это значение
        const isAlreadySelected =
            type === 'range'
                ? filter.value_min === 0 || filter.value_max === 0
                : entry.values.has(value);

        if (type === 'range') {
            // Диапазон значений
            entry.range = [filter.value_min, filter.value_max];
            const label =
                Number(filter.value_min).toLocaleString('ru') +
                ' руб. — ' +
                Number(filter.value_max).toLocaleString('ru') +
                ' руб.';

            entry.values = isAlreadySelected ? new Set() : new Set([label]);
        } else if (type === 'radio') {
            // Радио-кнопка (можно выбрать только одно значение), удаляем значение, (если оно уже было выбрано, снимаем выбор) или устанавливаем новое значение (одно единственное)
            entry.values = isAlreadySelected ? new Set() : new Set([value]);
        } else {
            // Чекбокс (множественный фильтр), удаляем значение, если оно уже было выбрано или добавляем значение, если его ещё нет
            isAlreadySelected ? entry.values.delete(value) : entry.values.add(value);
        }

        // Удаление зависимых фильтров
        if (Array.isArray(dependencies)) {
            for (const depKey of dependencies) {
                map.delete(depKey);
            }
        }

        // Если после действия значения пустые — удаляем весь фильтр
        if (entry.values.size === 0) {
            map.delete(key);
        }

        set({filtersMap: map});
    },

    getFilters: () => {
        const {filtersMap, staticFilterParams} = get();

        const filters = {};
        const selectedFilters = [];

        filtersMap.forEach((entry, key) => {
            const valuesArray = [];

            entry.values.forEach((value) => {
                if (entry.type === 'range') {
                    // filters[key] = {value_min: entry.range[0], value_max: entry.range[1]};
                    // Фильтры для отправки на сервер
                    valuesArray.push({
                        value_min: entry.range[0],
                        value_max: entry.range[1],
                    });
                } else {
                    // Фильтры для отправки на сервер
                    valuesArray.push({
                        name: key,
                        value,
                    });
                }

                // Выбранные фильтры
                selectedFilters.push({
                    key,
                    type: entry.type,
                    value,
                });
            });

            if (valuesArray.length) {
                filters[key] = valuesArray;
            }
        });

        set({
            selectedFilters,
            filterParams: {...staticFilterParams, ...filters},
        });
    },

    /**
     * Удаление одного выбранного фильтра
     */
    deleteFilter: (filter) => {
        const {changePagination, addOrRemoveFilter, getFilters, getSearchData} = get();

        // Для range сбрасываем значения
        if (filter.type === 'range') {
            filter = {
                ...filter,
                value_min: 0,
                value_max: 0,
            };
        }

        changePagination();
        addOrRemoveFilter(filter);
        getFilters();
        getSearchData();
    },

    /**
     * Сбросить все выбранные фильтры
     */
    deleteFilters: () => {
        const {changePagination, staticFilterParams, getSearchData} = get();

        changePagination();
        set({
            filtersMap: new Map(),
            selectedFilters: [],
            filterParams: staticFilterParams,
        });
        getSearchData();
    },

    /**
     * Сброс параметров перед запросом
     */
    resetParametersBeforeRequest: () => {
        const {searchQuery, searchQueryOutput, staticFilterParams} = get();
        if (searchQueryOutput !== '' && searchQuery !== searchQueryOutput) {
            set({
                filtersMap: new Map(), // Сбрасываем фильтры
                selectedFilters: [], // Сбрасываем выбранные фильтры
                filterParams: staticFilterParams, // Сбрасываем фильтры на дефолтную структуру
                activePage: 1, // Активная страница, фронт
                page: 1, // Активная страница, сервер
            });
        }
    }
}));
